import React from 'react';
import { cn } from '../../../lib/utils';
import { getIconSizeClass, ICON_INTENT_CLASSES } from './iconUtils';

// ═══════════════════════════════════════════════════════════════════════════════
// UNIFIED ICON SYSTEM — AX1 Design Governance
// ═══════════════════════════════════════════════════════════════════════════════
//
// Icon sizes are standardised to 7 tiers:
//   micro → 10px (radio-dot checkmarks, sub-micro indicators)
//   xxs   → 14px (compact buttons, field labels)
//   xs    → 12px (micro indicators, badge internals)
//   sm    → 16px (default inline / buttons / tables)
//   md    → 20px (sidebar baseline — DO NOT CHANGE)
//   lg    → 24px (section headers / emphasised inline)
//   xl    → 28px (empty states / feature highlights)
//
// Semantic intent controls colour only — never layout.
// Pages should import AppIcon from IconRegistry, not raw lucide components.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Icon size variants — 7 governed tiers.
 */
export type IconSize = 'micro' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Semantic intent — controls icon colour class.
 *
 * | Intent   | Tailwind class                     | Use case                          |
 * |----------|------------------------------------|-----------------------------------|
 * | default  | text-text-muted                      | Passive / decorative              |
 * | active   | text-text-primary   | Active nav items, headings        |
 * | muted    | text-text-muted                      | Disabled / placeholder            |
 * | primary  | text-primary-600                   | Brand CTA, selected state         |
 * | success  | text-status-success-foreground                   | Completed, approved               |
 * | warning  | text-status-warning-foreground                     | Pending, attention                |
 * | danger   | text-status-danger-foreground                       | Error, destructive                |
 * | info     | text-primary-600                      | Informational                     |
 */
export type IconIntent =
  | 'default'
  | 'active'
  | 'muted'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

/**
 * Props for the low-level Icon wrapper.
 *
 * Prefer `<AppIcon name="..." />` from the registry in page/feature code.
 * Use `<Icon icon={Component} />` only inside shared UI primitives.
 */
export interface IconProps {
  /** The Lucide (or SVG-compatible) component to render */
  icon: React.FC<React.SVGProps<SVGSVGElement>>;

  /**
   * Governed size tier
   * @default 'sm'
   */
  size?: IconSize;

  /**
   * Semantic intent — sets colour automatically.
   * When provided, overrides the deprecated `color` prop.
   */
  intent?: IconIntent;

  /**
   * Direct colour class override.
   * @deprecated Use `intent` for governed colours. Retained for migration.
   */
  color?: string;

  /** Additional Tailwind classes */
  className?: string;

  /**
   * Apply CSS `animate-spin` (e.g. loading spinners).
   * @default false
   */
  spin?: boolean;

  /**
   * Rotation in degrees. Applied as inline transform.
   * Useful for chevron open/close states.
   */
  rotate?: number;

  /**
   * ARIA label for standalone (decorative-free) icons.
   * Omit when the icon accompanies visible text.
   */
  ariaLabel?: string;
}

/**
 * Low-level Icon wrapper with governed sizing + semantic intent.
 *
 * @example
 * ```tsx
 * import { Search } from 'lucide-react';
 * <Icon icon={Search} size="sm" intent="muted" />
 * ```
 */
export const Icon: React.FC<IconProps> = ({
  icon: IconComponent,
  size = 'sm',
  intent,
  color,
  className,
  spin,
  rotate,
  ariaLabel,
  ...props
}) => {
  // intent takes precedence → legacy color → no colour class
  const colorClass = intent ? ICON_INTENT_CLASSES[intent] : color;

  const ariaProps = ariaLabel
    ? { 'aria-label': ariaLabel }
    : { 'aria-hidden': true as const };

  const style: React.CSSProperties | undefined =
    rotate != null ? { transform: `rotate(${rotate}deg)`, transition: 'transform 150ms ease' } : undefined;

  return (
    <IconComponent
      className={cn(
        getIconSizeClass(size),
        colorClass,
        spin && 'animate-spin',
        className,
      )}
      style={style}
      focusable="false"
      {...ariaProps}
      {...props}
    />
  );
};

/**
 * Custom UploadCloud icon (retained for backward compatibility)
 */
export const UploadCloud: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M16 16v-6m0 0l-4-4m4 4l4-4" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 4 16.3" />
    <polyline points="16 16 12 12 8 16" />
  </svg>
);
